/**
 * Centralized Query Keys
 * Single source of truth for all React Query cache keys
 * Prevents typos and makes cache invalidation easier
 *
 * Pattern:
 * - Use arrays for hierarchical keys: ['conversations', conversationId]
 * - Use 'as const' for type safety
 * - Group by feature/domain
 */
const CONVERSATION_ROOT = ["conversations"] as const;

export const queryKeys = {
  /** Session & auth keys */
  session: {
    all: ["session"] as const,
    status: () => [...queryKeys.session.all, "status"] as const,
    user: (userId: string) =>
      [...queryKeys.session.all, "user", userId] as const,
  },

  /** Canonical conversation keys */
  conversations: {
    all: CONVERSATION_ROOT,
    lists: () => [...queryKeys.conversations.all, "list"] as const,
    list: (filters?: Record<string, any>) =>
      [...queryKeys.conversations.lists(), filters] as const,
    details: () => [...queryKeys.conversations.all, "detail"] as const,
    detail: (conversationId: string) =>
      [...queryKeys.conversations.details(), conversationId] as const,
    objects: (conversationId: string) =>
      [...queryKeys.conversations.detail(conversationId), "objects"] as const,
    summary: (conversationId: string) =>
      [...queryKeys.conversations.detail(conversationId), "summary"] as const,
    /**
     * Server-assembled ConversationJson snapshot (#1282).
     * Keyed by workspace + conversation to preserve cache partitioning across
     * workspaces (a conversation id is unique per workspace, but the key was
     * historically a `[root, workspaceId, conversationId]` tuple — keep both).
     */
    json: (workspaceId: string | null, conversationId: string | null) =>
      [
        ...queryKeys.conversations.detail(conversationId ?? ""),
        "json",
        workspaceId,
      ] as const,
    activity: (conversationId: string) =>
      [...queryKeys.conversations.detail(conversationId), "activity"] as const,
    /**
     * First-paint persisted module-pane resolution (AuthenticatedAppShell Gate 6).
     * A SIBLING of `detail` (not nested under it) so the frequent
     * `invalidateQueries(detail(id))` calls don't prefix-match and needlessly
     * refetch this one-shot read.
     */
    initialPane: (conversationId: string) =>
      [...queryKeys.conversations.all, "initial-pane", conversationId] as const,
  },

  /** Workspace module keys */
  modules: {
    all: ["modules"] as const,
    list: (workspaceId: string) =>
      [...queryKeys.modules.all, "list", workspaceId] as const,
    sidebarPreferences: (workspaceId: string) =>
      [...queryKeys.modules.all, "sidebarPreferences", workspaceId] as const,
  },

  /** Object keys */
  objects: {
    all: ["objects"] as const,
    lists: () => [...queryKeys.objects.all, "list"] as const,
    list: (filters?: Record<string, any>) =>
      [...queryKeys.objects.lists(), filters] as const,
    details: () => [...queryKeys.objects.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.objects.details(), id] as const,
    byId: (id: string) => [...queryKeys.objects.detail(id)] as const,
    byType: (type: string) =>
      [...queryKeys.objects.all, "byType", type] as const,
  },

  /** Chat keys */
  chat: {
    all: ["chat"] as const,
    history: (userId: string) =>
      [...queryKeys.chat.all, "history", userId] as const,
    messages: (sessionId: string) =>
      [...queryKeys.chat.all, "messages", sessionId] as const,
  },

  /** Search keys */
  search: {
    all: ["search"] as const,
    hybrid: (query: string, filters?: Record<string, any>) =>
      [...queryKeys.search.all, "hybrid", query, filters] as const,
    semantic: (query: string, filters?: Record<string, any>) =>
      [...queryKeys.search.all, "semantic", query, filters] as const,
    agent: (query: string, filters?: Record<string, any>) =>
      [...queryKeys.search.all, "agent", query, filters] as const,
  },

  /** User settings */
  user: {
    all: ["user"] as const,
    session: () => [...queryKeys.user.all, "session"] as const,
    settings: () => [...queryKeys.user.all, "settings"] as const,
  },

  /** Graph/visualization keys */
  graph: {
    all: ["graph"] as const,
    visualization: (filters?: Record<string, any>) =>
      [...queryKeys.graph.all, "visualization", filters] as const,
  },

  /** Agent/tools keys */
  agent: {
    all: ["agent"] as const,
    config: () => [...queryKeys.agent.all, "config"] as const,
    query: (queryId: string) =>
      [...queryKeys.agent.all, "query", queryId] as const,
    enrichPlace: (placeId: string) =>
      [...queryKeys.agent.all, "enrichPlace", placeId] as const,
  },

  /** File upload keys */
  files: {
    all: ["files"] as const,
    upload: (jobId: string) =>
      [...queryKeys.files.all, "upload", jobId] as const,
  },

  /** Card keys */
  cards: {
    all: ["cards"] as const,
    byId: (cardId: string) => [...queryKeys.cards.all, cardId] as const,
    byObject: (objectId: string) =>
      [...queryKeys.cards.all, "object", objectId] as const,
  },

  /** Activities panel keys */
  activities: {
    all: (workspaceId: string) => ["activities", workspaceId] as const,
    tab: (workspaceId: string, tab: string) =>
      [...queryKeys.activities.all(workspaceId), tab] as const,
    badge: (workspaceId: string) =>
      [...queryKeys.activities.all(workspaceId), "badge"] as const,
  },

  /** Drive sync keys */
  driveSync: {
    status: (workspaceId?: string) =>
      ["driveSync", "status", workspaceId] as const,
    details: () => ["driveSync", "details"] as const,
    verify: () => ["driveSync", "verify"] as const,
  },

  /** Google integration keys */
  googleAccounts: {
    all: ["google-accounts"] as const,
    byModule: (moduleId: string, conversationId: string) =>
      ["google-accounts", moduleId, conversationId] as const,
  },

  /** Google Drive browse keys */
  gdriveBrowse: {
    all: ["gdriveBrowse"] as const,
    lists: () => [...queryKeys.gdriveBrowse.all, "list"] as const,
    list: (
      parentId: string | null,
      pageToken: string | null,
      accountId?: string | null,
    ) =>
      [
        ...queryKeys.gdriveBrowse.lists(),
        accountId ?? "default",
        parentId ?? "root",
        pageToken ?? "first",
      ] as const,
  },

  /** Email Intelligence Report keys */
  emailIntelligence: {
    all: ["emailIntelligence"] as const,
    report: (ws: string) =>
      [...queryKeys.emailIntelligence.all, "report", ws] as const,
    coverage: (ws: string) =>
      [...queryKeys.emailIntelligence.all, "coverage", ws] as const,
  },
  /** Agent jobs (Agents module) keys */
  agentJobs: {
    all: ["agentJobs"] as const,
    list: (workspaceId: string) =>
      [...queryKeys.agentJobs.all, "list", workspaceId] as const,
    detail: (id: string) => [...queryKeys.agentJobs.all, "detail", id] as const,
  },

  /** Agent trust (Agents redesign): roster, sign-off queue, grants */
  agentTrust: {
    all: ["agentTrust"] as const,
    roster: (workspaceId: string) =>
      [...queryKeys.agentTrust.all, "roster", workspaceId] as const,
    approvals: (workspaceId: string) =>
      [...queryKeys.agentTrust.all, "approvals", workspaceId] as const,
    /** Decided approvals (terminal outcomes) — the Notifications "Past" tab. */
    decidedApprovals: (workspaceId: string) =>
      [...queryKeys.agentTrust.all, "decided-approvals", workspaceId] as const,
    grants: (agentId: string) =>
      [...queryKeys.agentTrust.all, "grants", agentId] as const,
  },

  /** Synthesis module keys */
  synthesis: {
    all: ["synthesis"] as const,
    byWorkspace: (workspaceId: string) =>
      [...queryKeys.synthesis.all, workspaceId] as const,
    /** Per-tab cache key (people, work, history, travel, health, watchlist, overview). */
    tab: (workspaceId: string, tab: string) =>
      [...queryKeys.synthesis.all, workspaceId, tab] as const,
  },

  /** Contacts outbound sync (two-way Google Contacts) */
  contactsOutbound: {
    all: ["contactsOutbound"] as const,
    byIntegration: (integrationId: string) =>
      [...queryKeys.contactsOutbound.all, integrationId] as const,
  },

  /** Scope-aware capability statuses for an integration (ADR 017 D4). The sole
   *  consumer of GET /integrations/:id/capabilities — drives <ScopeUpgradePrompt>. */
  integrationCapabilities: {
    all: ['integrationCapabilities'] as const,
    byId: (integrationId: string) =>
      [...queryKeys.integrationCapabilities.all, integrationId] as const,
  },

  /** A workspace's integrations list (GET /workspaces/:ws/integrations) — the
   *  provider-agnostic V1+V2 union of every connected account. Drives the
   *  capability-enable UI (ADR 017 D4 / U2). */
  integrations: {
    all: ['integrations'] as const,
    list: (workspaceId: string) =>
      [...queryKeys.integrations.all, 'list', workspaceId] as const,
  },

  /** Onboarding recipe framework keys */
  onboarding: {
    all: ["onboarding"] as const,
    recipeSession: () => [...queryKeys.onboarding.all, "recipe-session"] as const,
    syncStatus: (workspaceId: string) =>
      [...queryKeys.onboarding.all, "sync-status", workspaceId] as const,
  },

  /** Contact candidate buckets (Potential / Flagged) */
  contactCandidates: {
    potential: (includeOneWay: boolean) =>
      ["contactCandidates", "potential", includeOneWay] as const,
    flagged: () => ["contactCandidates", "flagged"] as const,
  },

  /**
   * Contact discover-suggestions banner (gmail-sourced "discover"; #5556).
   * `byModule` is the invalidation prefix `refreshDiscoverSuggestions` bumps
   * across `allContacts.length` variants; `list` is the full per-count query
   * key (keyed by contact count, mirroring `useObjectsByType`).
   */
  contactDiscover: {
    byModule: (moduleId: string) => ["contactDiscover", moduleId] as const,
    list: (moduleId: string, contactCount: number) =>
      [...queryKeys.contactDiscover.byModule(moduleId), contactCount] as const,
  },

  /**
   * Contact merge-candidate count banner ("N possible duplicates"; #5556).
   * `byModule` is the invalidation prefix `refreshMergeCandidates` bumps across
   * `contactCount` variants; `list` is the full per-count query key.
   */
  contactMergeCandidates: {
    byModule: (moduleId: string) =>
      ["contactMergeCandidates", moduleId] as const,
    list: (moduleId: string, contactCount: number) =>
      [
        ...queryKeys.contactMergeCandidates.byModule(moduleId),
        contactCount,
      ] as const,
  },

  /** Public profile / pages keys */
  pub: {
    mySlug: () => ['pub', 'mySlug'] as const,
  },

  /** Per-workspace entitlement projection (ADR 042) */
  entitlements: {
    all: ['entitlements'] as const,
    map: (workspaceId: string) =>
      [...queryKeys.entitlements.all, workspaceId] as const,
  },

  /** Campaigns module keys */
  campaigns: {
    all: ['campaigns'] as const,
    list: (workspaceId: string) =>
      [...queryKeys.campaigns.all, workspaceId] as const,
    detail: (workspaceId: string, campaignId: string) =>
      ['campaign-detail', workspaceId, campaignId] as const,
    lists: (workspaceId: string) =>
      ['campaign-lists', workspaceId] as const,
  },

  /** Studio v2 brand kits (library + editor, Wave C) */
  brandKits: {
    all: ['brandKits'] as const,
    list: (workspaceId: string) =>
      [...queryKeys.brandKits.all, 'list', workspaceId] as const,
    default: (workspaceId: string) =>
      [...queryKeys.brandKits.all, 'default', workspaceId] as const,
    usage: (id: string) => [...queryKeys.brandKits.all, 'usage', id] as const,
  },

  /** Payment / billing config keys (ENABLE_TEAM_BILLING gate, #4836) */
  payment: {
    config: () => ['payment', 'config'] as const,
  },

  /** Email templates (Phase 1: /workspaces/:ws/email-templates) */
  emailTemplates: {
    all: ['emailTemplates'] as const,
    list: (workspaceId: string) =>
      [...queryKeys.emailTemplates.all, 'list', workspaceId] as const,
    detail: (workspaceId: string, id: string) =>
      [...queryKeys.emailTemplates.all, 'detail', workspaceId, id] as const,
  },

  /**
   * Channels module (#5574) — group channels, DMs, workspace/space members,
   * per-channel membership, and per-channel message lists. Cross-mount cache
   * so re-entering the module paints the last-seen channel + messages
   * instantly instead of the pre-#5483 "Loading channels…" flash, while a
   * background refresh (existing poll/visibilitychange effects) keeps it
   * fresh. Data is written by imperative loaders via `setQueryData` rather
   * than query-driven `queryFn`s (the loaders own fetch timing/gating), so
   * every entry's cache key must match exactly between reader and writer.
   */
  channels: {
    all: (workspaceId: string) => ['channels', workspaceId] as const,
    list: (workspaceId: string) =>
      [...queryKeys.channels.all(workspaceId), 'list'] as const,
    dmList: (workspaceId: string) =>
      [...queryKeys.channels.all(workspaceId), 'dm-list'] as const,
    /**
     * Keyed by space id (falls back to workspace id) — a channel's member
     * roster. Today `spaceId` always resolves to the same value as
     * `workspaceId` (ChannelsModule derives both from the same
     * `selectedContainer`), so in practice this key nests under
     * `channels.all(workspaceId)` too — it's just spelled independently
     * (not built from `channels.all`) as future-proofing in case a "space"
     * ever becomes a distinct concept from the workspace. NOTE: because it's
     * not built from `channels.all(...)`, a prefix invalidation against
     * `channels.all(...)` will NOT touch this entry today — invalidate it
     * explicitly if a future "refresh this workspace" sweep is added.
     */
    members: (spaceId: string) => ['channels', spaceId, 'members'] as const,
    channelMembers: (workspaceId: string, channelId: string) =>
      [...queryKeys.channels.all(workspaceId), 'channel-members', channelId] as const,
    messages: (workspaceId: string, channelId: string) =>
      [...queryKeys.channels.all(workspaceId), 'messages', channelId] as const,
  },

} as const;

/** Every param that changes what an objects-by-type list query returns (#5259). */
export interface ObjectsByTypeQueryKeyParams {
  conversationId?: string;
  moduleId?: string;
  limit?: number;
  query?: string;
  startAfter?: string;
  startBefore?: string;
  expandRecurring?: boolean;
  includeDriveCache?: boolean;
  includeArchived?: boolean;
  /** Provider-integration id scoping a contacts list to one address book (§3.4). */
  source?: string;
  /** Contacts: also include synced (source-linked) contacts in a module list (§3.4). */
  includeSourceLinked?: boolean;
  status?: string;
  cursor?: string;
}

/**
 * THE cache key for objects-by-type list queries (#5259). Both
 * `useObjectsByType` implementations (features/objects/hooks.ts and
 * features/objects/hooks/queries.ts) and every manual
 * setQueryData / invalidateQueries / prefetchQuery against those lists MUST
 * build keys through this function so patches land on the entry the hooks
 * actually read. Segment order is load-bearing: `mod:<id>` stays directly
 * after the byType prefix because callers (drive rename, legacy optimistic
 * mutations) prefix-match / `.includes('mod:<id>')` on it.
 */
export function objectsByTypeQueryKey(
  type: string,
  params: ObjectsByTypeQueryKeyParams = {},
) {
  const {
    conversationId,
    moduleId,
    limit = 50,
    query,
    startAfter,
    startBefore,
    expandRecurring,
    includeDriveCache,
    includeArchived,
    source,
    includeSourceLinked,
    status,
    cursor,
  } = params;
  return [
    ...queryKeys.objects.byType(type),
    moduleId ? `mod:${moduleId}` : "workspace",
    limit,
    query,
    startAfter,
    startBefore,
    expandRecurring === false ? "noexpand" : undefined,
    includeDriveCache ? "drivecache" : undefined,
    source ? `src:${source}` : undefined,
    includeSourceLinked ? "srcLinked" : undefined,
    status,
    // conversationId only scopes the fetch when no moduleId is set (module
    // scope wins in both hook implementations), so only then is it in the key.
    !moduleId && conversationId ? `conv:${conversationId}` : undefined,
    cursor,
    includeArchived ? "archived" : "active",
  ].filter(Boolean);
}

export function invalidateConversation(
  queryClient: any,
  conversationId: string,
) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.conversations.detail(conversationId),
  });
}

export function invalidateObject(queryClient: any, objectId: string) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.objects.detail(objectId),
  });
}

export function invalidateSearch(queryClient: any) {
  queryClient.invalidateQueries({ queryKey: queryKeys.search.all });
}
